import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getImpiantoSettings, updateImpiantoSettings } from '../api/impianto_settings';
import i18n from '../i18n';
import CerRifiutiProdottiSettings from '../components/settings/CerRifiutiProdottiSettings';
import TipologieRifiutoProdottoSettings from '../components/settings/TipologieRifiutoProdottoSettings';
import CerIngressoSettings from '../components/settings/CerIngressoSettings';
import CerEowSettings from '../components/settings/CerEowSettings';
import UsciteMaterialeLavoratoSettings from '../components/settings/UsciteMaterialeLavoratoSettings';
import CategorieSettings from '../components/settings/CategorieSettings';
import PrezziVenditaSettings from '../components/settings/PrezziVenditaSettings';
import DatiFatturazioneSettings from '../components/settings/DatiFatturazioneSettings';
import DiagnosticaLedgerSection from '../components/settings/DiagnosticaLedgerSection';
import { useAuthStore } from '../store/authStore';
import { canWriteSettings, canViewCategoriePrezzi, canViewDiagnostica } from '../lib/constants';
import { fmtKg } from '../lib/saldo';

type Tab = 'anagrafica' | 'dati_fatturazione' | 'categorie' | 'capacita' | 'cer_ingresso' | 'cer_eow' | 'cer_rifiuti' | 'tipologie_rifiuti' | 'uscite_materiale' | 'diagnostica';

// Navigazione a SIDEBAR per gruppi (E2). "Soglie permanenza" rimosso (E1, config
// dormiente a DB, nessun consumatore runtime).
const GRUPPI: { labelKey: string; items: { key: Tab; labelKey: string }[] }[] = [
  { labelKey: 'settings.gruppo_impianto', items: [
    { key: 'anagrafica', labelKey: 'settings.tab_anagrafica' },
    { key: 'capacita', labelKey: 'settings.tab_capacita' },
  ] },
  { labelKey: 'settings.gruppo_fatturazione', items: [
    { key: 'dati_fatturazione', labelKey: 'settings.tab_dati_fatturazione' },
    { key: 'categorie', labelKey: 'settings.tab_categorie' },
  ] },
  { labelKey: 'settings.gruppo_cer', items: [
    { key: 'cer_ingresso', labelKey: 'settings.tab_cer_ingresso' },
    { key: 'cer_eow', labelKey: 'settings.tab_cer_eow' },
    { key: 'cer_rifiuti', labelKey: 'settings.tab_cer_rifiuti' },
    { key: 'tipologie_rifiuti', labelKey: 'settings.tab_tipologie_rifiuti' },
    { key: 'uscite_materiale', labelKey: 'settings.tab_uscite' },
  ] },
  { labelKey: 'settings.gruppo_sistema', items: [
    { key: 'diagnostica', labelKey: 'settings.tab_diagnostica' },
  ] },
];

/** Valori accettati per ?tab=: un parametro sconosciuto non deve rompere la pagina. */
const TAB_VALIDI = new Set<Tab>(GRUPPI.flatMap((g) => g.items.map((i) => i.key)));

const anagraficaSchema = z.object({
  ragione_sociale: z.string().min(1, i18n.t('settings.val_obbligatorio')),
  partita_iva: z.string().optional(),
  codice_fiscale: z.string().optional(),
  sede_via: z.string().optional(),
  sede_comune: z.string().optional(),
  sede_provincia: z.string().optional(),
  sede_cap: z.string().optional(),
  numero_aua: z.string().optional(),
  capacita_totale_kg: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional()
  ),
});
type AnagraficaForm = z.infer<typeof anagraficaSchema>;

const capacitaSchema = z.object({
  capacita_stoccaggio_kg: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional()
  ),
  capacita_aua_annua_kg: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional()
  ),
  capacita_lavorazione_giornaliera_kg: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional()
  ),
  percentuale_utilizzo_lavorazione: z.coerce.number().int().min(0).max(100),
});
type CapacitaForm = z.infer<typeof capacitaSchema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white disabled:bg-stone-bg disabled:text-ink-soft disabled:cursor-not-allowed disabled:border-stone-mid';

export default function SettingsPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  // Scrittura Settings = {admin, DIR, RT}; AMM sola lettura (niente bottoni salva).
  const canWrite = canWriteSettings(role);
  // "Categorie e prezzi": informazione commerciale → AMM esclusa anche in vista.
  const canViewCategorie = canViewCategoriePrezzi(role);
  // Deep-link dalla Dashboard: /settings?tab=diagnostica. Solo il valore
  // iniziale — da lì in poi il tab è stato locale, come prima.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const richiesto = searchParams.get('tab');
    return TAB_VALIDI.has(richiesto as Tab) ? (richiesto as Tab) : 'anagrafica';
  });
  // Difesa: se il tab non è consentito al ruolo, ripiega su 'anagrafica'.
  // MAPPA e non catena di ternari: con due tab a RBAC propria il ternario
  // annidato è il modo di dimenticarsi il terzo.
  const tabConsentito: Partial<Record<Tab, boolean>> = {
    categorie: canViewCategorie,
    diagnostica: canViewDiagnostica(role),
  };
  const effectiveTab: Tab = tabConsentito[tab] === false ? 'anagrafica' : tab;
  const [loading, setLoading] = useState(true);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const anagraficaForm = useForm({ resolver: zodResolver(anagraficaSchema) });
  const capacitaForm = useForm({ resolver: zodResolver(capacitaSchema) });
  const [capacitaEffettiva, setCapacitaEffettiva] = useState<number | null>(null);

  useEffect(() => {
    getImpiantoSettings()
      .then((data) => {
        anagraficaForm.reset({
          ragione_sociale: data.ragione_sociale,
          partita_iva: data.partita_iva ?? '',
          codice_fiscale: data.codice_fiscale ?? '',
          sede_via: data.sede_via ?? '',
          sede_comune: data.sede_comune ?? '',
          sede_provincia: data.sede_provincia ?? '',
          sede_cap: data.sede_cap ?? '',
          numero_aua: data.numero_aua ?? '',
          capacita_totale_kg: data.capacita_totale_kg ?? undefined,
        });
        capacitaForm.reset({
          capacita_stoccaggio_kg: data.capacita_stoccaggio_kg ?? undefined,
          capacita_aua_annua_kg: data.capacita_aua_annua_kg ?? undefined,
          capacita_lavorazione_giornaliera_kg: data.capacita_lavorazione_giornaliera_kg ?? undefined,
          percentuale_utilizzo_lavorazione: data.percentuale_utilizzo_lavorazione ?? 70,
        });
        setCapacitaEffettiva(data.capacita_giornaliera_effettiva_kg ?? null);
      })
      .catch(() => setSaveError(t('settings.errore_caricamento')))
      .finally(() => setLoading(false));
    // I form RHF hanno reference stabili: l'effect resta di fatto mount-once.
  }, [anagraficaForm, capacitaForm, t]);

  // Il messaggio "salvato" si spegne da solo dopo 3s. Il timer sta QUI e non
  // nei due gestori di salvataggio: li' erano due `setTimeout` senza
  // `clearTimeout`, e dopo lo smontaggio scattavano lo stesso chiamando
  // `setSaveOk` su un componente che non c'e' piu' — vitest usciva con 1 anche
  // a test tutti verdi, in modo dipendente dal carico. Un solo effetto per
  // entrambi: la condizione e' lo stato, non chi l'ha scritto.
  useEffect(() => {
    if (!saveOk) return;
    const timer = setTimeout(() => setSaveOk(null), 3000);
    return () => clearTimeout(timer);
  }, [saveOk]);

  async function saveAnagrafica(data: unknown) {
    const d = data as AnagraficaForm;
    setSaveOk(null);
    setSaveError(null);
    try {
      await updateImpiantoSettings(d);
      setSaveOk(t('settings.salvato_ok'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSaveError(detail ?? t('settings.errore_salvataggio'));
    }
  }

  async function saveCapacita(data: unknown) {
    const d = data as CapacitaForm;
    setSaveOk(null);
    setSaveError(null);
    try {
      const updated = await updateImpiantoSettings({
        capacita_stoccaggio_kg: d.capacita_stoccaggio_kg,
        capacita_aua_annua_kg: d.capacita_aua_annua_kg,
        capacita_lavorazione_giornaliera_kg: d.capacita_lavorazione_giornaliera_kg,
        percentuale_utilizzo_lavorazione: Number(d.percentuale_utilizzo_lavorazione),
      });
      setCapacitaEffettiva(updated.capacita_giornaliera_effettiva_kg ?? null);
      setSaveOk(t('settings.capacita_salvata'));
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSaveError(detail ?? t('settings.errore_salvataggio'));
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Settings size={20} className="text-ink-soft" />
        <h1 className="text-xl font-semibold text-ink-dark">{t('settings.titolo')}</h1>
      </div>

      <div className="flex gap-6 items-start">
        {/* Sidebar verticale a gruppi */}
        <nav className="w-56 flex-shrink-0 space-y-4">
          {GRUPPI.map((g) => (
            <div key={g.labelKey}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint px-2 mb-1">{t(g.labelKey)}</p>
              <div className="space-y-0.5">
                {/* Stessa mappa usata da effectiveTab: la voce sparisce e, se
                    qualcuno ci arriva via ?tab=, il fallback la respinge. */}
                {g.items.filter((it) => tabConsentito[it.key] !== false).map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    data-testid={`settings-tab-${it.key}`}
                    onClick={() => setTab(it.key)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${effectiveTab === it.key ? 'bg-sage-light text-sage-dark font-medium' : 'text-ink-mid hover:bg-stone-bg'}`}
                  >
                    {t(it.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Contenuto sezione */}
        <div className="flex-1 min-w-0">
      {saveOk && (
        <div data-testid="settings-save-ok" className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{saveOk}</div>
      )}
      {saveError && (
        <div className="mb-4 p-3 bg-accent-danger-bg border border-[#FECACA] rounded-lg text-sm text-accent-danger">{saveError}</div>
      )}

      {loading ? (
        <div className="text-center py-8 text-sm text-ink-soft">{t('settings.caricamento')}</div>
      ) : effectiveTab === 'anagrafica' ? (
        <form onSubmit={anagraficaForm.handleSubmit(saveAnagrafica)} className="space-y-4 bg-white border border-stone-mid rounded-xl p-5">
          {/* fieldset disabled per chi non scrive (AMM): campi in sola lettura visiva */}
          <fieldset disabled={!canWrite} className="space-y-4 m-0 p-0 border-0 min-w-0">
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.ragione_sociale')}</label>
            <input type="text" className={inputCls} {...anagraficaForm.register('ragione_sociale')} />
            {anagraficaForm.formState.errors.ragione_sociale && <p className="text-xs text-accent-danger mt-1">{anagraficaForm.formState.errors.ragione_sociale.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.partita_iva')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('partita_iva')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.codice_fiscale')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('codice_fiscale')} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.via')}</label>
            <input type="text" className={inputCls} {...anagraficaForm.register('sede_via')} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.comune')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('sede_comune')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.provincia')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('sede_provincia')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.cap')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('sede_cap')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.numero_aua')}</label>
              <input type="text" className={inputCls} {...anagraficaForm.register('numero_aua')} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.anagrafica.capacita_totale')}</label>
            <input type="number" step="0.01" className={inputCls} {...anagraficaForm.register('capacita_totale_kg')} />
          </div>
          </fieldset>
          {canWrite && (
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={anagraficaForm.formState.isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
                {anagraficaForm.formState.isSubmitting ? t('settings.salvataggio') : t('settings.salva_modifiche')}
              </button>
            </div>
          )}
        </form>
      ) : effectiveTab === 'capacita' ? (
        <form onSubmit={capacitaForm.handleSubmit(saveCapacita)} className="space-y-4 bg-white border border-stone-mid rounded-xl p-5">
          <fieldset disabled={!canWrite} className="space-y-4 m-0 p-0 border-0 min-w-0">
          <p className="text-sm text-ink-mid">
            {t('settings.capacita.intro')}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.capacita.stoccaggio')}</label>
              <input type="number" step="0.01" data-testid="capacita-stoccaggio-input" className={inputCls} placeholder="es. 500000" {...capacitaForm.register('capacita_stoccaggio_kg')} />
              <p className="text-xs text-ink-faint mt-0.5">{t('settings.capacita.stoccaggio_hint')}</p>
              {capacitaForm.formState.errors.capacita_stoccaggio_kg && <p className="text-xs text-accent-danger mt-1">{capacitaForm.formState.errors.capacita_stoccaggio_kg.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.capacita.aua')}</label>
              <input type="number" step="0.01" data-testid="capacita-aua-input" className={inputCls} placeholder="es. 1000000" {...capacitaForm.register('capacita_aua_annua_kg')} />
              <p className="text-xs text-ink-faint mt-0.5">{t('settings.capacita.aua_hint')}</p>
              {capacitaForm.formState.errors.capacita_aua_annua_kg && <p className="text-xs text-accent-danger mt-1">{capacitaForm.formState.errors.capacita_aua_annua_kg.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.capacita.lavorazione')}</label>
              <input type="number" step="0.01" data-testid="capacita-lavorazione-input" className={inputCls} placeholder="es. 80000" {...capacitaForm.register('capacita_lavorazione_giornaliera_kg')} />
              <p className="text-xs text-ink-faint mt-0.5">{t('settings.capacita.lavorazione_hint')}</p>
              {capacitaForm.formState.errors.capacita_lavorazione_giornaliera_kg && <p className="text-xs text-accent-danger mt-1">{capacitaForm.formState.errors.capacita_lavorazione_giornaliera_kg.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('settings.capacita.pct')}</label>
              <input type="number" data-testid="capacita-pct-input" min="0" max="100" className={inputCls} {...capacitaForm.register('percentuale_utilizzo_lavorazione')} />
              <p className="text-xs text-ink-faint mt-0.5">{t('settings.capacita.pct_hint')}</p>
              {capacitaForm.formState.errors.percentuale_utilizzo_lavorazione && <p className="text-xs text-accent-danger mt-1">{capacitaForm.formState.errors.percentuale_utilizzo_lavorazione.message}</p>}
            </div>
          </div>
          {capacitaEffettiva != null && (
            <div className="p-3 bg-sage/10 border border-sage/20 rounded-lg">
              <p className="text-sm font-medium text-sage-dark">
                {t('settings.capacita.effettiva_label')} <span className="font-bold">{t('settings.capacita.effettiva_val', { kg: fmtKg(capacitaEffettiva) })}</span>
              </p>
              <p className="text-xs text-ink-soft mt-0.5">
                {t('settings.capacita.effettiva_calc')}
              </p>
            </div>
          )}
          </fieldset>
          {canWrite && (
            <div className="flex justify-end pt-2">
              <button type="submit" data-testid="settings-capacita-save" disabled={capacitaForm.formState.isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
                {capacitaForm.formState.isSubmitting ? t('settings.salvataggio') : t('settings.capacita.salva')}
              </button>
            </div>
          )}
        </form>
      ) : effectiveTab === 'dati_fatturazione' ? (
        <DatiFatturazioneSettings />
      ) : effectiveTab === 'categorie' ? (
        /* Due blocchi, un posto solo: quanto FACCIAMO PAGARE (conferimenti) e
           quanto VENDIAMO (rifiuti prodotti, per tipologia). Chi cerca un
           prezzo viene qui, qualunque dei due cerchi. */
        <div className="space-y-5">
          <CategorieSettings />
          <PrezziVenditaSettings />
        </div>
      ) : effectiveTab === 'cer_ingresso' ? (
        <CerIngressoSettings />
      ) : effectiveTab === 'cer_eow' ? (
        <CerEowSettings />
      ) : effectiveTab === 'cer_rifiuti' ? (
        <CerRifiutiProdottiSettings />
      ) : effectiveTab === 'tipologie_rifiuti' ? (
        <TipologieRifiutoProdottoSettings />
      ) : effectiveTab === 'diagnostica' ? (
        <DiagnosticaLedgerSection />
      ) : (
        <UsciteMaterialeLavoratoSettings />
      )}
        </div>
      </div>
    </div>
  );
}
